import React from 'react';
import Button from '@mui/material/Button';
import { ExpandMore } from '@mui/icons-material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { makeStyles } from 'src/makeStyles';
import { colors, fontSize } from '../../constants/styleConstants';

const useStyles = makeStyles()(() => ({
  content: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
    color: colors.textColor,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
  },
  selected: {
    borderRadius: '8px',
    border: `solid 1px ${colors.blue3}`,
    backgroundColor: colors.blue1,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
    color: colors.blue3,
    fontWeight: 'bold',
  },
  icon: {
    color: colors.blue3,
    fontSize: fontSize.large,
  },
  customStyle: {
    textTransform: 'none',
    fontSize: fontSize.smallTitle,
    margin: '2px 4px 2px 0px',
    marginRight: '8px',
  },
  label: {
    marginLeft: '8px',
  },
  popper: {
    zIndex: '998',
    maxWidth: '400px',
  },
  menuList: {
    whiteSpace: 'break-spaces',
  },
}));

const DropdownButton = ({ label, onChange, options, selected }) => {
  const { classes, cx } = useStyles();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [selectedIndex, setSelectedIndex] = React.useState(selected || options?.[0]?.value || 0);
  const handleMenuItemClick = option => {
    onChange && onChange(option);
    setSelectedIndex(option?.value);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const menuOptions = options?.length > 0 && (
    <MenuList id="split-button-menu">
      {options.map(option => (
        <MenuItem
          className={classes.menuList}
          key={option.id}
          selected={option.value === selectedIndex}
          onClick={() => handleMenuItemClick(option)}
        >
          {option.label}
        </MenuItem>
      ))}
    </MenuList>
  );

  return (
    <>
      {options?.length > 0 && (
        <Button
          size="small"
          aria-expanded={open ? 'true' : undefined}
          aria-label={label}
          aria-haspopup="menu"
          onClick={handleToggle}
          className={cx(open ? classes.selected : classes.content, classes.customStyle)}
          ref={anchorRef}
        >
          <span className={classes.label}>
            {options?.filter(item => item.value === selectedIndex)[0]?.label || options[0]?.label}
          </span>
          <ExpandMore className={classes.icon} />
        </Button>
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        className={classes.popper}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>{menuOptions}</ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default React.memo(DropdownButton);
